export interface EventListenOptions<T> {
	once?: boolean;
}

export class ZEvent {
	constructor(public readonly type: string) {}

	public freeze(): this {
		Object.freeze(this);
		return this;
	}
}

export class ZEventTarget<E extends ZEventsMap, K extends keyof E = keyof E> {
	private events: Map<string, ZEventListener[]>;

	constructor() {
		this.events = new Map();
	}

	getNumberListener() {
		return this.events.size;
	}

	/**
	 * Sets up a function that will be called whenever the specified event is delivered to the target
	 */
	addEventListener<T extends K>(
		type: T,
		listener: ZEventListener<E[T]>,
		options?: EventListenOptions<E[T]>
	): () => void {
		const strType = type.toString();
		let listeners = this.events.get(strType);
		if (!listeners) {
			listeners = [];
			this.events.set(strType, listeners);
		}
		const removeListener = () => this.removeEventListener(type, listener);
		if (options?.once === true) {
			const originListener = listener;
			listener = (event) => {
				removeListener();
				return originListener(event);
			};
		}
		listeners.push(listener);

		return removeListener;
	}

	/**
	 * Sets up a function that will be called whenever the specified event is delivered to the target
	 */
	addEventListenerOnce<T extends K>(type: T, listener: ZEventListener<E[T]>): () => void {
		return this.addEventListener(type, listener, { once: true });
	}

	/**
	 * Removes from the EventTarget an event listener previously registered with EventTarget.addEventListener()
	 */
	removeEventListener<T extends K>(type: T, listenerToRemove: ZEventListener<E[T]>): void {
		const strType = type.toString();
		const listeners = this.events.get(strType);
		if (!listeners) return;

		const filterListeners = (listener: ZEventListener) => listener !== listenerToRemove;
		this.events.set(strType, listeners.filter(filterListeners));
	}

	/**
	 * Removes from the EventTarget all events listener previously registered with EventTarget.addEventListener()
	 */
	removeAllEventListener() {
		this.events.clear();
	}

	/**
	 * Dispatches an Event at the specified EventTarget, (synchronously) invoking the affected EventListeners in the appropriate order
	 */
	dispatchEvent(event: E[K]): void {
		let listeners = this.events.get(event.type);
		if (!listeners || listeners.length === 0) return;
		listeners = listeners.slice();
		for (let i = 0; i < listeners.length; i++) {
			const listener = listeners[i];
			if (listener) listener(event);
		}
	}

	/**
	 * Dispatches an Event if there are any EventListeners subscribed to it.
	 */
	smartDispatchEvent<T extends K>(eventType: T, eventFactory: () => E[T]) {
		const listeners = this.events.get(eventType as string);
		if (listeners === undefined || listeners.length === 0) return;

		const event = eventFactory();
		this.dispatchEvent(event);
	}
}

export type ZEventsMap = {
	[name: string]: ZEvent;
};
export interface ZEventListener<E extends ZEvent = any> {
	(evt: E): void;
}
